import React, { useCallback } from "react";
import { PlaidConnection } from "../../common/types/plaidConnection";
import { ButtonWithLoader } from "../general/ButtonWithLoader/ButtonWithLoader";
import {
    useAddPlaidAccountsMutation,
    usePlaidConnectionFixingMutation,
} from "../../mutations/plaidConnection";
import { ExtendedButtonProps } from "../general/Button/Button";
import { PLAID_PAYPAL_INSTITUTION_ID } from "../../common/helpers/plaid";
import { CreatePayPalConnection } from "../paypal/CreatePayPalConnection";
import { useTypedFlags } from "../../hooks/useTypedFlags";

interface Props extends Omit<ExtendedButtonProps, "onClick"> {
    connection: PlaidConnection;
    accountChurned?: boolean;
    onFixed?: () => void;
}

export const FixPlaidConnection: React.FC<Props> = ({
    connection,
    onFixed,
    accountChurned,
    children,
    ...btnProps
}) => {
    const fixAccount = usePlaidConnectionFixingMutation(connection, onFixed);
    const addAccounts = useAddPlaidAccountsMutation(onFixed);
    const { payPalMigration, payPalIntegration } = useTypedFlags();

    const fix = useCallback(() => {
        if (accountChurned) {
            addAccounts.mutate(connection);
        } else {
            fixAccount.mutate();
        }
    }, [accountChurned, addAccounts, connection, fixAccount]);

    if (
        connection.institutionId === PLAID_PAYPAL_INSTITUTION_ID &&
        payPalMigration &&
        payPalIntegration
    ) {
        return (
            <CreatePayPalConnection
                plaidConnectionToMigrate={connection}
                btnVariant={btnProps.variant}
                btnSize={btnProps.size}
            >
                {children}
            </CreatePayPalConnection>
        );
    }

    return (
        <ButtonWithLoader
            onClick={fix}
            loading={fixAccount.isPending || addAccounts.isPending}
            {...btnProps}
        >
            {children}
        </ButtonWithLoader>
    );
};
